import clsx from 'clsx';
import React, { useMemo } from 'react';

import { QuotedMessage as DefaultQuotedMessage, isMessageEdited, MessageStatus } from 'stream-chat-react';
import { isOnlyEmojis, messageHasAttachments } from 'stream-chat-react';

import {
  useComponentContext,
  useMessageContext,
  useTranslationContext,
} from 'stream-chat-react';
import { renderText as defaultRenderText } from 'stream-chat-react';

import type { LocalMessage, TranslationLanguages } from 'stream-chat';
import type { MessageContextValue } from 'stream-chat-react';
import { CustomMessageErrorText } from './CustomMessageErrorText';

import { localeFormatDate } from '../../../utils/Utils';
import { useAuth } from '../../../hooks/useAuth';


export type MessageTextProps = {
  onClickMessage?: (event: React.MouseEvent) => void;
  /* Replaces the CSS class name placed on the component's inner `div` container */
  customInnerClass?: string;
  /* Adds a CSS class name to the component's outer `div` container */
  customWrapperClass?: string;
  /* The `StreamChat` message object, which provides necessary data to the underlying UI components (overrides the value stored in `MessageContext`) */
  message?: LocalMessage;
  /* Theme string to be added to CSS class names */
  theme?: string;
  isMyMessage?: boolean;
} & Pick<MessageContextValue, 'renderText'>;

const UnMemoizedMessageTextComponent = (props: MessageTextProps) => {
  const {
    customInnerClass,
    customWrapperClass = '',
    message: propMessage,
    renderText: propsRenderText,
    theme = 'simple',
    onClickMessage,
    isMyMessage,
  } = props;

  const { QuotedMessage = DefaultQuotedMessage } = useComponentContext('MessageText');

  const {
    message: contextMessage,
    onMentionsClickMessage,
    onMentionsHoverMessage,
    renderText: contextRenderText,
    unsafeHTML,
  } = useMessageContext('MessageText');

  const renderText = propsRenderText ?? contextRenderText ?? defaultRenderText;

  const { userLanguage } = useTranslationContext('MessageText');
  const message = propMessage || contextMessage;
  const hasAttachment = messageHasAttachments(message);

  let messageDate = message.created_at.toLocaleString(localeFormatDate, { hour: '2-digit', minute: '2-digit' })

  let messageTextToRender =
    message.i18n?.[`${userLanguage}_text` as `${TranslationLanguages}_text`] ||
    message.text;

  let nbChars = messageDate.length + 5;
  if(isMyMessage)
    nbChars += 8;
  messageTextToRender += ' ';
  for(let i = 0; i < nbChars; i++) {
    messageTextToRender += '&nbsp;';
  }

  const messageText = useMemo(
    () => renderText(messageTextToRender, message.mentioned_users),
    [message.mentioned_users, messageTextToRender],
  );

  const wrapperClass = customWrapperClass || 'str-chat__message-text';
  const innerClass =
    customInnerClass ||
    `str-chat__message-text-inner str-chat__message-${theme}-text-inner`;

    let classNameMessageText = 'str-chat__message-text-message inline';
  //const isEdited = isMessageEdited(message)
  const isEdited = false;
  if(isEdited) { 
    classNameMessageText += ' with-edited';
  }
  if(isMyMessage) {
    classNameMessageText += ' with-my-message';
  } 

  if (!messageTextToRender && !message.quoted_message) return null;

  return (
    <div className={wrapperClass} tabIndex={0}>
      <div
        className={clsx(innerClass, {
          [`str-chat__message-${theme}-text-inner--has-attachment`]: hasAttachment,
          [` str-chat__message-${theme}-text-inner--is-emoji`]:
            isOnlyEmojis(message.text) && !message.quoted_message,
        })}
        data-testid='message-text-inner-wrapper'
        onClick={onMentionsClickMessage}
        onMouseOver={onMentionsHoverMessage}
      >
        {message.quoted_message && <QuotedMessage />}
        <CustomMessageErrorText message={message} theme={theme} />
        {unsafeHTML && message.html ? (
            <div className={classNameMessageText} dangerouslySetInnerHTML={{ __html: message.html }} onClick={onClickMessage} />
        ) : (
            <div className={classNameMessageText} onClick={onClickMessage}>{messageText}</div>
        )}
        <div className="absolute bottom-0 right-0 flex flex-row items-center gap-1 px-1 pb-1 str-chat__message-custom-status" onClick={onClickMessage}>
            {isEdited && <span>Edited</span>}
            <div>{messageDate}</div>
            <MessageStatus 
                MessageReadStatus={() => <MessageReadStatus />}
                MessageDeliveredStatus={() => <span className="material-symbols-outlined cursor-default text-[18px]" title="Delivered">check</span>}
            />
        </div>
      </div>
    </div>
  );
};

export const CustomMessageText = React.memo(
  UnMemoizedMessageTextComponent,
) as typeof UnMemoizedMessageTextComponent;

const MessageReadStatus = () => {
    const { readBy } = useMessageContext('MessageReadStatus');
    const {pleb} = useAuth();

    const readersWithoutOwnUser = readBy
        ? readBy.filter((item) => item.id !== pleb?.id)
        : [];

    const readByTxt = readersWithoutOwnUser.length > 1 ? `Read by ${readersWithoutOwnUser.length} people` : 'Read';

    return <div className="flex flex-row items-center cursor-default" title={readByTxt}>
        {readersWithoutOwnUser.length > 1 ? <span className="text-brand-orange">{readersWithoutOwnUser.length}&nbsp;</span> : null}
        <span className="material-symbols-outlined text-brand-orange text-[18px]">done_all</span>
    </div>
}